
// ─── Binance kline parser ─────────────────────────────────────────────────────
/**
 * parseBinanceKlines
 *
 * Converts raw Binance kline arrays into the app's internal candle format.
 *
 * Binance returns each candle as a 12-element array:
 *   [0]  openTime        (ms timestamp)
 *   [1]  open            (string)
 *   [2]  high            (string)
 *   [3]  low             (string)
 *   [4]  close           (string)
 *   [5]  volume          (string, base asset volume)
 *   [6]  closeTime       (ms timestamp)
 *   [7]  quoteVolume     (string, quote asset volume — USD for USDT pairs)
 *   [8]  numTrades       (number)
 *   [9]  takerBuyBase    (string)
 *   [10] takerBuyQuote   (string)
 *   [11] ignore          (string)
 *
 * Output format: [timestamp, open, high, low, close, volume]
 *   All price/volume values are parsed to floats.
 *   This matches the format expected by extractPrices() and the TA engine.
 *
 * @param {Array[]} rawKlines  Raw array from Binance /api/v3/klines
 * @returns {Array[]}          [[timestamp, open, high, low, close, volume], ...]
 */
export function parseBinanceKlines(rawKlines) {
  if (!rawKlines || !Array.isArray(rawKlines) || rawKlines.length === 0) {
    return [];
  }

  return rawKlines.map(k => [
    k[0],               // openTime (ms) — already a number from Binance
    parseFloat(k[1]),   // open
    parseFloat(k[2]),   // high
    parseFloat(k[3]),   // low
    parseFloat(k[4]),   // close
    parseFloat(k[5]),   // volume (base asset)
  ]);
}

/**
 * parseBinanceKlinesForSparkline
 *
 * Returns a lightweight [timestamp, close] array from Binance klines,
 * matching the format CoinGecko's market_chart.prices used to provide.
 * Useful for the SparklineChart component which only needs close prices.
 *
 * @param {Array[]} rawKlines  Raw array from Binance /api/v3/klines
 * @returns {Array[]}          [[timestamp, close], ...]
 */
export function parseBinanceKlinesForSparkline(rawKlines) {
  if (!rawKlines || !Array.isArray(rawKlines) || rawKlines.length === 0) {
    return [];
  }

  return rawKlines.map(k => [
    k[0],               // openTime (ms)
    parseFloat(k[4]),   // close price
  ]);
}

// ─── Existing resampling functions (kept for fallback use) ────────────────────
// Note: With Binance, these are largely redundant since you can request
// 4h/1d/1w candles natively. They remain for any edge cases where
// resampling is still needed (e.g. building weekly from daily).

// Resample hourly data to 4-hour candles
export function resampleTo4H(hourlyData) {
  if (!hourlyData || hourlyData.length === 0) {
    return [];
  }

  const fourHourCandles = [];
  const candleInterval = 4; // 4 hours

  for (let i = 0; i < hourlyData.length; i += candleInterval) {
    const candleData = hourlyData.slice(i, i + candleInterval);
    
    if (candleData.length === 0) continue;

    // Create 4H candle from hourly data
    // Assuming hourlyData is array of [timestamp, price] arrays
    const timestamp = candleData[0][0]; // Use first hour's timestamp
    const open = candleData[0][1];
    const close = candleData[candleData.length - 1][1];
    const high = Math.max(...candleData.map(d => d[1]));
    const low = Math.min(...candleData.map(d => d[1]));

    fourHourCandles.push([timestamp, open, high, low, close]);
  }

  return fourHourCandles;
}

// Resample hourly data to daily candles (24 hours each)
export function resampleToDaily(hourlyData) {
  if (!hourlyData || hourlyData.length === 0) {
    return [];
  }

  const dailyCandles = [];
  const msPerDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  // Group hourly data by UTC day
  const dayGroups = {};

  hourlyData.forEach(dataPoint => {
    const timestamp = dataPoint[0];
    const utcDate = new Date(timestamp);
    
    // Get UTC day boundary (midnight UTC)
    const dayKey = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate()).getTime();
    
    if (!dayGroups[dayKey]) {
      dayGroups[dayKey] = [];
    }
    dayGroups[dayKey].push(dataPoint);
  });

  // Convert groups to daily candles
  Object.keys(dayGroups).sort((a, b) => parseInt(a) - parseInt(b)).forEach(dayKey => {
    const dayData = dayGroups[dayKey];
    
    if (dayData.length === 0) return;

    const timestamp = parseInt(dayKey);
    const prices = dayData.map(d => d[1]);
    const open = prices[0];
    const close = prices[prices.length - 1];
    const high = Math.max(...prices);
    const low = Math.min(...prices);

    dailyCandles.push([timestamp, open, high, low, close]);
  });

  return dailyCandles;
}

// Resample daily data to weekly candles (7 days each)
export function resampleToWeekly(dailyData) {
  if (!dailyData || dailyData.length === 0) {
    return [];
  }

  const weeklyCandles = [];
  const msPerWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

  // Group daily data by UTC week (starting Monday)
  const weekGroups = {};

  dailyData.forEach(dataPoint => {
    const timestamp = dataPoint[0];
    const date = new Date(timestamp);
    
    // Find Monday of the week (ISO week starts on Monday)
    const dayOfWeek = date.getUTCDay();
    const monday = new Date(date);
    const daysFromMonday = (dayOfWeek === 0) ? 6 : dayOfWeek - 1; // Sunday = 0, so 6 days from Monday
    monday.setUTCDate(date.getUTCDate() - daysFromMonday);
    monday.setUTCHours(0, 0, 0, 0);
    
    const weekKey = monday.getTime();
    
    if (!weekGroups[weekKey]) {
      weekGroups[weekKey] = [];
    }
    weekGroups[weekKey].push(dataPoint);
  });

  // Convert groups to weekly candles
  Object.keys(weekGroups).sort((a, b) => parseInt(a) - parseInt(b)).forEach(weekKey => {
    const weekData = weekGroups[weekKey];
    
    if (weekData.length === 0) return;

    const timestamp = parseInt(weekKey);
    const prices = weekData.map(d => d[1]);
    const open = prices[0];
    const close = prices[prices.length - 1];
    const high = Math.max(...prices);
    const low = Math.min(...prices);

    weeklyCandles.push([timestamp, open, high, low, close]);
  });

  return weeklyCandles;
}

// Extract price arrays from candle data for indicator calculations
export function extractPrices(candleData) {
  if (!candleData || candleData.length === 0) {
    return {
      timestamps: [],
      opens: [],
      highs: [],
      lows: [],
      closes: []
    };
  }

  return {
    timestamps: candleData.map(candle => candle[0]),
    opens: candleData.map(candle => candle[1]),
    highs: candleData.map(candle => candle[2]),
    lows: candleData.map(candle => candle[3]),
    closes: candleData.map(candle => candle[4])
  };
}

// Convert simple price array to OHLC format (for APIs that return only prices)
export function convertPricesToOHLC(priceData, interval = '1h') {
  if (!priceData || priceData.length === 0) {
    return [];
  }

  const ohlcData = [];
  
  // If price data is already in [timestamp, price] format
  if (Array.isArray(priceData[0])) {
    priceData.forEach(([timestamp, price]) => {
      // Create OHLC where O=H=L=C=price (since we only have closing prices)
      ohlcData.push([timestamp, price, price, price, price]);
    });
  } else {
    // If it's just an array of prices, create synthetic timestamps
    priceData.forEach((price, index) => {
      const timestamp = Date.now() - ((priceData.length - 1 - index) * 60 * 60 * 1000); // Hourly intervals
      ohlcData.push([timestamp, price, price, price, price]);
    });
  }

  return ohlcData;
}

// Get the latest complete candle (excluding current incomplete candle)
export function getLatestCompleteCandle(candleData, timeframe = '1h') {
  if (!candleData || candleData.length < 2) {
    return null;
  }

  // Return second-to-last candle as the latest complete one
  return candleData[candleData.length - 2];
}

// Align timestamps to candle boundaries
export function alignToCandleBoundary(timestamp, intervalMinutes) {
  const date = new Date(timestamp);
  const intervalMs = intervalMinutes * 60 * 1000;
  
  // Round down to nearest interval boundary
  const alignedTimestamp = Math.floor(timestamp / intervalMs) * intervalMs;
  
  return alignedTimestamp;
}

// Check if candle is complete based on current time
export function isCandleComplete(candleTimestamp, intervalMinutes) {
  const intervalMs = intervalMinutes * 60 * 1000;
  const candleEndTime = candleTimestamp + intervalMs;
  
  return Date.now() >= candleEndTime;
}
